import { useEffect, useState } from 'react';
import { Tag, Tags, Ban, CornerLeftUp } from 'lucide-react';

const BARVA = '#F59E0B';

export const POPIS_STITKU = {
    vlastni: 'Vlastní štítek',
    skupina: 'Společný štítek',
    bez: 'Bez štítku',
};

/** Čitelný popis nastavení — „Společný štítek (Přílohy)". */
export function popisStitku(n) {
    if (!n?.stitek) return POPIS_STITKU.vlastni;
    return n.stitek === 'skupina' ? `${POPIS_STITKU.skupina} (${n.stitek_skupina || 'Přílohy'})` : POPIS_STITKU[n.stitek];
}

/** Nejbližší úroveň s vlastní hodnotou (kategorie → rodiče), jinak výchozí vlastní štítek. */
export function zdedenyStitek(retez) {
    const k = retez.find(x => x && x.stitek);
    return k ? { stitek: k.stitek, stitek_skupina: k.stitek_skupina } : { stitek: 'vlastni', stitek_skupina: null };
}

/**
 * Štítek na krabici pro rozvoz a výdej (2026-10-23).
 *
 *   Vlastní   — každý kus má svůj štítek „1/3" (výchozí),
 *   Společný  — jeden štítek se soupisem pro celou skupinu (Přílohy…),
 *               vytiskne se, až je hotová celá skupina objednávky,
 *   Bez       — štítek se netiskne; rozvozce položku vidí při kontrole
 *               u regálu přímo i s variantou.
 *
 * Nastavuje se na kategorii, dědí se do podkategorií a produktů a produkt
 * to může přebít. `value` = { stitek, stitek_skupina }, stitek null = dědí.
 */
export default function StitekPolozky({ value, zdedeno, nazevZdedeni = 'Podle kategorie', navrhySkupin = [], onChange, isDark }) {
    const muted = isDark ? 'rgba(255,255,255,0.4)' : 'var(--text-muted)';
    const border = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    const rezim = value?.stitek ?? null;
    const [skupina, setSkupina] = useState(value?.stitek_skupina || 'Přílohy');
    useEffect(() => { setSkupina(value?.stitek_skupina || 'Přílohy'); }, [value?.stitek_skupina]);

    const volby = [
        { id: null, nazev: `${nazevZdedeni} (${popisStitku(zdedeno)})`, Ikona: CornerLeftUp },
        { id: 'vlastni', nazev: POPIS_STITKU.vlastni, Ikona: Tag },
        { id: 'skupina', nazev: POPIS_STITKU.skupina, Ikona: Tags },
        { id: 'bez', nazev: POPIS_STITKU.bez, Ikona: Ban },
    ];
    const navrhy = [...new Set(['Přílohy', ...navrhySkupin.filter(Boolean)])];
    const ulozSkupinu = () => {
        const t = skupina.trim() || 'Přílohy';
        if (t !== (value?.stitek_skupina || 'Přílohy') || value?.stitek_skupina == null) {
            onChange({ stitek: 'skupina', stitek_skupina: t });
        }
    };

    return (
        <div className="rounded-xl px-4 py-3" style={{
            background: isDark ? 'rgba(255,255,255,0.03)' : 'var(--surface-base)',
            border: `1px solid ${border}`,
        }}>
            <div className="text-sm font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Štítek na krabici</div>
            <p className="text-[11px] mb-2" style={{ color: muted }}>
                Rozvoz a výdej. Vlastní = každý kus má svůj štítek 1/3. Společný = jeden štítek se soupisem pro celou
                skupinu (třeba Přílohy), vyjede, až je hotová celá skupina. Bez štítku = rozvozce položku vidí při
                kontrole u regálu přímo i s variantou.
            </p>
            <div className="flex flex-wrap gap-1.5">
                {volby.map(v => {
                    const on = rezim === v.id;
                    return (
                        <button key={String(v.id)} type="button"
                            onClick={() => onChange(v.id === 'skupina'
                                ? { stitek: 'skupina', stitek_skupina: skupina.trim() || 'Přílohy' }
                                : { stitek: v.id, stitek_skupina: null })}
                            className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium"
                            style={{
                                background: on ? `${BARVA}26` : 'transparent',
                                border: `1px solid ${on ? BARVA : border}`,
                                color: on ? BARVA : 'var(--text-primary)', cursor: 'pointer',
                            }}>
                            <v.Ikona size={12} />
                            {v.nazev}
                        </button>
                    );
                })}
            </div>
            {rezim === 'skupina' && (
                <div className="flex flex-wrap items-center gap-2 mt-2.5">
                    <span className="text-[11px] font-semibold" style={{ color: muted }}>Skupina</span>
                    <input
                        list="stitek-skupiny"
                        value={skupina}
                        onChange={e => setSkupina(e.target.value.slice(0, 40))}
                        onBlur={ulozSkupinu}
                        onKeyDown={e => e.key === 'Enter' && e.currentTarget.blur()}
                        placeholder="Přílohy"
                        aria-label="Skupina společného štítku"
                        className="rounded-lg px-2.5 text-xs"
                        style={{ height: 30, minWidth: 160, border: `1px solid ${BARVA}88`, background: 'transparent', color: 'var(--text-primary)', outline: 'none' }}
                    />
                    <datalist id="stitek-skupiny">
                        {navrhy.map(n => <option key={n} value={n} />)}
                    </datalist>
                    <span className="text-[11px]" style={{ color: muted }}>Položky se stejnou skupinou jdou na jeden štítek.</span>
                </div>
            )}
        </div>
    );
}
